/**
 * The single source of client-side gating.
 *
 * Everything here resolves against the ACTIVE org. That settles a real bug:
 * `_dashboard.tsx` used to compute `canManage` from *any* membership, so a
 * syndic of résidence A saw the full management nav while acting inside
 * résidence B, where they are a plain member — every one of those links then
 * 403'd server-side.
 *
 * This is display gating only. The server is the enforcement layer; hiding a
 * control here is a courtesy, never a security boundary.
 */
import type { Me } from "~/lib/use-me";
import { useMe } from "~/lib/use-me";

export interface Permissions {
  /** `member.role` in the active org: owner | syndic | proprietaire | gardien. */
  activeRole: string | undefined;
  /** Gately staff — holds `owner` in at least one org. */
  isOwner: boolean;
  /** Belongs on the web console at all (owner or syndic anywhere). */
  isStaff: boolean;
  /** Manages this org: its accounts, units, gates, forms, réglages. */
  canManage: boolean;
  /**
   * Sees org-wide activity and the analytics home. A syndic only gets it when
   * a Gately owner granted `viewAllActivity` — a résidence can withhold it so
   * its syndic cannot read every résident's comings and goings.
   */
  canViewActivity: boolean;
  /** Manages the org's account roster (`/accounts`). */
  canManageOrgAccounts: boolean;
  /**
   * May mark an expected guest as arrived without a scan (« Arrivé », i.e.
   * `scan.record` source=confirm). Mirrors that handler's server-side guard:
   * gate staff always, plus whoever may read every pass — a Gately `owner`,
   * or a syndic the résidence granted `viewAllPasses`. A résident never can,
   * which is why the button is hidden rather than left to 403.
   */
  canConfirmArrival: boolean;
  /**
   * May add someone to a roster: an org manager, or the owner/admin of a unit
   * (an office responsable adding an employee to their own company).
   */
  canInviteTeamMember: boolean;
  /**
   * Can actually COMPLETE the "Nouvelle invitation" form: the org's syndic
   * (residence-level passes), or someone attached to at least one unit.
   * Gately staff (`owner`) are deliberately excluded — staff never create
   * passes on behalf of a customer (GATELY-58, decision 1); `gardien` has no
   * unit and no residence power, so the form is a dead end for them too.
   */
  canCreateInvite: boolean;
  /** Belongs to a company/foyer in the active org. */
  hasUnit: boolean;
  /** The org has the Réservations module (`amenities`) turned on. */
  hasAmenities: boolean;
  /** Manages it: `hasAmenities` and `canManage`. */
  canManageAmenities: boolean;
}

/** The boolean flags — the only ones a nav gate may name. */
export type PermissionFlag = {
  [K in keyof Permissions]: Permissions[K] extends boolean ? K : never;
}[keyof Permissions];

/** Pure; exported for tests and for route `loader`s, which have no hooks. */
export function resolvePermissions(me: Me): Permissions {
  const activeRole = me.memberships.find(
    (m) => m.orgId === me.activeOrganizationId,
  )?.role;

  const canManage = activeRole === "syndic" || activeRole === "owner";
  const hasUnit = me.activeUnits.length > 0;

  return {
    activeRole,
    isOwner: me.isOwner,
    isStaff: me.isStaff,
    canManage,
    canViewActivity:
      me.isOwner ||
      (activeRole === "syndic" && me.activeOrgPermissions.viewAllActivity),
    canManageOrgAccounts: canManage,
    canConfirmArrival:
      activeRole === "gardien" ||
      activeRole === "owner" ||
      (activeRole === "syndic" && me.activeOrgPermissions.viewAllPasses),
    canInviteTeamMember:
      canManage ||
      me.activeUnits.some((u) => u.role === "owner" || u.role === "admin"),
    canCreateInvite: activeRole === "syndic" || hasUnit,
    hasUnit,
    hasAmenities: me.activeOrgFeatures.includes("amenities"),
    canManageAmenities: canManage && me.activeOrgFeatures.includes("amenities"),
  };
}

export function usePermissions(): Permissions {
  return resolvePermissions(useMe());
}
